import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/FormPage.css';

const EnquiryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [formData, setFormData] = useState({
    company_name: '',
    full_name: '',
    email: '',
    mobile: '',
    address: '',
    enquiry_details: '',
    domain: window.location.hostname,
    file_link: '',
  });

  useEffect(() => {
    if (isEditMode) {
      loadEnquiry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadEnquiry = async () => {
    try {
      setLoading(true);
      const response = await api.getEnquiry(id);
      const enquiry = response.data;
      setFormData({
        company_name: enquiry.company_name || '',
        full_name: enquiry.full_name || '',
        email: enquiry.email || '',
        mobile: enquiry.mobile || '',
        address: enquiry.address || '',
        enquiry_details: enquiry.enquiry_details || '',
        domain: enquiry.domain || window.location.hostname,
        file_link: enquiry.file_link || '',
      });
    } catch (err) {
      setError('Failed to load enquiry');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setUploading(true);
    setError('');

    try {
      const response = await api.uploadFile(file);
      if (response.success && response.file_link) {
        setFormData({ ...formData, file_link: response.file_link });
        setSuccess(`File uploaded: ${response.filename}`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to upload file');
      setSelectedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setFormData({ ...formData, file_link: '' });
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      if (isEditMode) {
        await api.updateEnquiry(id, formData);
        setSuccess('Enquiry updated successfully!');
      } else {
        await api.createEnquiry(formData);
        setSuccess('Enquiry created successfully!');
      }

      setTimeout(() => {
        navigate('/enquiries');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to save enquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/enquiries');
  };

  if (loading && isEditMode) {
    return (
      <div className="form-page-container">
        <div className="loading">Loading enquiry...</div>
      </div>
    );
  }

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <div>
          <h1>{isEditMode ? 'Edit Enquiry' : 'Create New Enquiry'}</h1>
          <p className="form-page-subtitle">
            {isEditMode ? 'Update enquiry information' : 'Add a new customer enquiry'}
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="form-section-title">Company Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Enter company name"
                />
              </div>

              <div className="form-group">
                <label>Domain</label>
                <input
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="company.com"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Contact Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Contact person name"
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="email@example.com"
                />
              </div>

              <div className="form-group">
                <label>Mobile *</label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="+91 1234567890"
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Business address"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Enquiry Details</h3>
            <div className="form-group">
              <label>Enquiry Details *</label>
              <textarea
                name="enquiry_details"
                value={formData.enquiry_details}
                onChange={handleChange}
                rows="6"
                required
                disabled={loading}
                placeholder="Describe the enquiry in detail..."
              />
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Attachments</h3>
            <div className="form-group">
              <label>Upload File</label>
              <input
                type="file"
                onChange={handleFileSelect}
                disabled={uploading || loading}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              <small>Supported: PDF, DOC, DOCX, JPG, PNG (Max 5MB)</small>
              
              {uploading && <p style={{marginTop: '10px', color: '#1890ff'}}>Uploading...</p>}
              
              {formData.file_link && (
                <div style={{marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <a href={formData.file_link} target="_blank" rel="noopener noreferrer">
                    View File
                  </a>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="btn btn-sm btn-danger"
                    disabled={loading}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading || uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || uploading}
            >
              {loading ? 'Saving...' : (isEditMode ? 'Update Enquiry' : 'Create Enquiry')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnquiryForm;

